import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from './Common/PageHeader';
import { StatsSkeleton, TableSkeleton } from './Common/SkeletonLoader';
import apiService from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const safeCount = (response) => {
  if (!response) return null;
  if (Array.isArray(response)) return response.length;
  // Handle paginated envelope shapes e.g. { items: [...], totalCount: n }
  if (typeof response.totalCount === 'number') return response.totalCount;
  if (typeof response.total === 'number') return response.total;
  if (Array.isArray(response.items)) return response.items.length;
  return null;
};

const formatStatus = (s) => {
  if (typeof s === 'number') {
    return ({ 0: 'pending', 1: 'confirmed', 2: 'cancelled', 3: 'completed' }[s]) || 'pending';
  }
  return (s || 'pending').toLowerCase();
};

const formatDateTime = (dt) => {
  if (!dt) return '—';
  try {
    const d = new Date(dt);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
    if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow, ${time}`;
    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${time}`;
  } catch {
    return '—';
  }
};

// ─── Main Component ─────────────────────────────────────────────────────────

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  const [appointments, setAppointments] = useState([]);
  const [apptLoading, setApptLoading] = useState(true);
  const [apptError, setApptError] = useState(false);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(false);
    try {
      const [customers, practitioners, services, offices] = await Promise.all([
        apiService.getCustomers().catch(() => null),
        apiService.getPractitioners().catch(() => null),
        apiService.getServices().catch(() => null),
        apiService.getOffices().catch(() => null),
      ]);

      if (!customers && !practitioners && !services && !offices) {
        setStatsError(true);
      } else {
        setStats({
          customers: safeCount(customers),
          practitioners: safeCount(practitioners),
          services: safeCount(services),
          locations: safeCount(offices),
        });
      }
    } catch {
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchAppointments = useCallback(async () => {
    setApptLoading(true);
    setApptError(false);
    try {
      const data = await apiService.getAppointments();
      const list = Array.isArray(data)
        ? data
        : (data?.items || data?.appointments || []);
      setAppointments(list);
    } catch {
      setApptError(true);
    } finally {
      setApptLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAppointments();
  }, [fetchStats, fetchAppointments]);

  return (
    <div className="dashboard">
      <PageHeader pageKey="dashboard">
        <button className="btn btn-secondary">
          <span className="icon icon--export icon--default"></span>
          Export
        </button>
      </PageHeader>

      {/* Stats Grid */}
      {statsLoading ? (
        <StatsSkeleton count={4} />
      ) : statsError ? (
        <div className="api-error-state">
          <div className="api-error-state__icon">⚠️</div>
          <h3>Could not load dashboard stats</h3>
          <p>There was a problem connecting to the server. Please check your connection and try again.</p>
          <button className="btn btn-primary" onClick={fetchStats}>Retry</button>
        </div>
      ) : (
        <div className="stats-grid">
          <StatCard
            number={stats?.customers ?? '—'}
            label="Total Customers"
            accentColor="var(--accent-turquoise)"
            icon="👥"
          />
          <StatCard
            number={stats?.practitioners ?? '—'}
            label="Active Practitioners"
            accentColor="var(--accent-gold)"
            icon="🩺"
          />
          <StatCard
            number={stats?.services ?? '—'}
            label="Active Services"
            accentColor="var(--primary-800)"
            icon="📋"
          />
          <StatCard
            number={stats?.locations ?? '—'}
            label="Locations"
            accentColor="var(--accent-rose)"
            icon="📍"
          />
        </div>
      )}

      {/* Charts Row — placeholder until analytics endpoint is available */}
      <div className="dashboard-charts-row">
        <ChartPlaceholder title="Booking Trend" subtitle="Last 6 months" />
        <ChartPlaceholder title="Revenue" subtitle="Last 6 months" />
      </div>

      {/* Upcoming Appointments */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Upcoming Appointments</h3>
          {!apptLoading && !apptError && appointments.length > 0 && (
            <span className="card-header-meta">{appointments.length} scheduled</span>
          )}
        </div>
        <div className="card-content">
          {apptLoading ? (
            <TableSkeleton rows={5} columns={4} hasAvatar={true} />
          ) : apptError ? (
            <div className="api-error-state">
              <div className="api-error-state__icon">⚠️</div>
              <h3>Could not load appointments</h3>
              <p>There was a problem connecting to the server. Please check your connection and try again.</p>
              <button className="btn btn-primary" onClick={fetchAppointments}>Retry</button>
            </div>
          ) : appointments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📅</div>
              <h3 className="empty-state-title">No upcoming appointments</h3>
              <p className="empty-state-description">Appointments will appear here once they are scheduled.</p>
            </div>
          ) : (
            <div className="activity-list">
              {appointments.slice(0, 10).map((appt, index) => (
                <ActivityItem key={appt.id || index} appointment={appt} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ number, label, accentColor, icon }) => (
  <div className="stat-card" style={{ '--card-accent': accentColor }}>
    <div className="stat-card-accent-bar" />
    <div className="stat-content">
      <div className="stat-header-row">
        <div className="stat-icon-bubble" style={{ background: `${accentColor}18` }}>
          <span className="stat-icon-glyph">{icon}</span>
        </div>
      </div>
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

const ChartPlaceholder = ({ title, subtitle }) => (
  <div className="dashboard-chart-card">
    <div className="dashboard-chart-header">
      <div>
        <h3 className="dashboard-chart-title">{title}</h3>
        <p className="dashboard-chart-subtitle">{subtitle}</p>
      </div>
    </div>
    <div className="dashboard-chart-body dashboard-chart-empty">
      <span className="dashboard-chart-empty-icon">📊</span>
      <p className="dashboard-chart-empty-text">Analytics data not yet available</p>
    </div>
  </div>
);

const ActivityItem = ({ appointment }) => {
  // Defensively handle various API field name conventions
  const patientName =
    appointment.customerName ||
    appointment.customer ||
    appointment.patientName ||
    appointment.patient ||
    'Unknown Patient';

  const practitionerName =
    appointment.practitionerName ||
    appointment.practitioner ||
    'Unknown Practitioner';

  const serviceName =
    appointment.serviceName ||
    appointment.service ||
    appointment.serviceType ||
    'Appointment';

  const startTime =
    appointment.startTime ||
    appointment.appointmentDate ||
    appointment.scheduledAt ||
    appointment.date;

  const statusStr = formatStatus(appointment.status);
  const initials = patientName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="activity-item">
      <div className="activity-avatar">{initials}</div>
      <div className="activity-main">
        <div className="activity-patient">{patientName}</div>
        <div className="activity-details">
          <span className="activity-service">{serviceName}</span>
          <span className="activity-separator">·</span>
          <span className="activity-practitioner">{practitionerName}</span>
        </div>
      </div>
      <div className="activity-right">
        <div className="activity-time">{formatDateTime(startTime)}</div>
        <span className={`status-badge status-${statusStr}`}>{statusStr}</span>
      </div>
    </div>
  );
};

export default Dashboard;
